/**
 * Academy domain — `AcademyQnaChatStore` state shape. Remote-only, in-memory.
 * Q&A questions, chat backlog and reaction counts are bucketed by live-event id.
 * Live SENDING is the SignalR hub's job; this store only holds REST reads + moderation.
 */
import type { Guid } from './academy-enums.model';
import type { LiveQuestionResponse } from './academy.model';
import type {
  ChatHistoryItem,
  ChatViewerItem,
  MutedParticipantItem,
  ReactionCounts,
} from './academy-responses.model';

export interface AcademyQnaChatState {
  /**
   * Viewer question backlog per live event: `GET /LiveEvents/{id}/questions` (every account's
   * questions on a single-room live, the viewer's own account on a live from before backend
   * 014, each with `PersonName`), followed by the questions the viewer asked since, as echoed
   * by `POST /LiveQuestions`.
   */
  questionsByEvent: Record<Guid, LiveQuestionResponse[]>;
  /** Viewer chat history per live event (`GET /LiveEvents/{id}/chat/history`), in arrival order. */
  chatByEvent: Record<Guid, ChatViewerItem[]>;
  /**
   * Producer studio question backlog per live event — `GET /LiveQuestions/producer` (all
   * tenants, with `PersonName`). Kept apart from `questionsByEvent`, which holds the viewer's
   * tenant-scoped backlog.
   */
  producerQuestionsByEvent: Record<Guid, LiveQuestionResponse[]>;
  /** Producer studio chat history per live event — `GET /LiveEvents/{id}/chat` (all tenants). */
  producerChatByEvent: Record<Guid, ChatHistoryItem[]>;
  /**
   * Who is muted in the chat per live event, as the producer studio last read it
   * (`GET /LiveEvents/{id}/chat/muted`). The hub's later mute changes live in
   * `AcademyRealtimeStore.muteChangesByEvent`.
   */
  mutedByEvent: Record<Guid, MutedParticipantItem[]>;
  /** Aggregated reaction counts per live event. */
  reactionsByEvent: Record<Guid, ReactionCounts>;
}

export const initialAcademyQnaChatState: AcademyQnaChatState = {
  questionsByEvent: {},
  chatByEvent: {},
  producerQuestionsByEvent: {},
  producerChatByEvent: {},
  mutedByEvent: {},
  reactionsByEvent: {},
};
